import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, Container, Grid, GridCol, Group, TextInput } from '@mantine/core';
import {
  deleteWithdrawSetDetails,
  IWithdrawSetRequest,
  IWithdrawSets,
  updateWithdrawSetDetails
} from '../../../libraries/brisa';
import { StatusType } from '../../../libraries/zps';
import { AppState } from '../../client-redux/reducers';
import ModalComponent from '../../components/modal';
import { Language } from '../../languages/languageHandler';
import ValuesTable from './withdraw-values-table';
import DisplayField from '../../components/display-field';
import { useStatusOperDialog } from '../../context/status-oper-dialog';

interface AddEditwithdrawSetProps {
  withdrawSet?: IWithdrawSets;
  withdrawSetList?: IWithdrawSets[];
  defaultEntid?: number;
  mode?: 'new' | 'edit';
  opened: boolean;
  close: () => void;
  onSaved?: () => void;
}

const AddEditWithdrawSet = ({
  withdrawSet,
  withdrawSetList,
  defaultEntid,
  mode,
  opened,
  close,
  onSaved
}: AddEditwithdrawSetProps) => {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const { showStatusOperDialog, handleApiError } = useStatusOperDialog();

  const controller = useRef(new AbortController()).current;
  const [formData, setFormData] = useState<IWithdrawSetRequest>({
    description: '',
    entid: 0,
    id: 0,
    mc: []
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (mode === 'new') {
      const newId = withdrawSetList?.length ? Math.max(...withdrawSetList.map(w => w.id)) + 1 : 1;

      setFormData({
        description: '',
        entid: defaultEntid || 0,
        id: newId,
        mc: []
      });
    } else if (withdrawSet) {
      setFormData({
        description: withdrawSet.description,
        entid: withdrawSet.entid,
        id: withdrawSet.id,
        mc: withdrawSet.mc || []
      });
    }
  }, [withdrawSet, mode, withdrawSetList, defaultEntid]);

  const handleChange = (field: keyof IWithdrawSetRequest, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setLoading(true);

    try {
      const response = await updateWithdrawSetDetails(formData, controller);

      if (response?.data.statusOper?.code === 0) {
        showStatusOperDialog({
          type: StatusType.INFO,
          message: mode === 'new' ? lang.BRISA_WITHDRAW_SETS_ADD_SUCCESS : lang.BRISA_WITHDRAW_SETS_EDIT_SUCCESS,
          statusOper: response.data.statusOper
        });

        onSaved?.();
        close();
      } else {
        showStatusOperDialog({
          type: StatusType.ERROR,
          message: lang.GLOBAL_ERROR,
          statusOper: response?.data.statusOper
        });
      }
    } catch (err) {
      handleApiError(err, lang.GLOBAL_ERROR);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!formData.entid || !formData.id) return;

    setLoading(true);

    try {
      const response = await deleteWithdrawSetDetails(formData.entid, formData.id);

      if (response?.data.statusOper?.code === 0) {
        showStatusOperDialog({
          type: StatusType.INFO,
          message: lang.BRISA_WITHDRAW_SETS_DELETE_SUCCESS,
          statusOper: response.data.statusOper
        });

        onSaved?.();
        close();
      } else {
        showStatusOperDialog({
          type: StatusType.ERROR,
          message: lang.GLOBAL_ERROR,
          statusOper: response?.data.statusOper
        });
      }
    } catch (err) {
      handleApiError(err, lang.GLOBAL_ERROR);
    } finally {
      setLoading(false);
    }
  };

  const title = mode === 'new' ? lang.BRISA_WITHDRAW_SETS_ADD : `${lang.BRISA_WITHDRAW_SETS_EDIT} - ${withdrawSet?.id}`;

  return (
    <ModalComponent
      show={opened}
      handleClick={close}
      title={title}
      content={
        <Container className="withdraw-set-modal">
          <Grid className="withdraw-details">
            <GridCol span={{ base: 12, xs: 6, sm: 6, md: 4 }}>
              <DisplayField title={lang.BRISA_WITHDRAW_SETS_ENTITY} content={formData.entid} />
            </GridCol>

            <GridCol span={{ base: 12, xs: 6, sm: 6, md: 4 }}>
              <DisplayField title={lang.BRISA_WITHDRAW_SETS_ID} content={formData.id} />
            </GridCol>

            <GridCol span={{ base: 12, xs: 8, sm: 8, md: 8 }}>
              <TextInput
                label={lang.BRISA_WITHDRAW_SETS_DESCRIPTION}
                value={formData.description}
                onChange={e => handleChange('description', e.currentTarget.value)}
                disabled={loading}
              />
            </GridCol>

            <GridCol className="values-table-container">
              <ValuesTable mc={formData.mc} onChange={newMc => handleChange('mc', newMc)} />
            </GridCol>
          </Grid>

          <Group className="buttons">
            <Button onClick={handleSave} loading={loading}>
              {lang.GLOBAL_SAVE_BUTTON}
            </Button>

            <Button onClick={handleDelete} loading={loading} color="red" disabled={!formData.entid || !formData.id}>
              {lang.GLOBAL_DELETE}
            </Button>
          </Group>
        </Container>
      }
    />
  );
};

export default AddEditWithdrawSet;
